import { useEffect, useState } from 'react';
import { Camera } from 'lucide-react';
import { LwButton, LwStatusPill, type LwStatusTone } from '@/components/lw';
import { avatarColor, initials } from '@/components/lw/format';

export type UserProfileIdentityMode = 'admin' | 'partner_delegated' | 'self';

export interface UserProfileIdentityHeaderProps {
  photoUrl?: string | null;
  name: string;
  statusLabel: string;
  statusTone: LwStatusTone;
  title?: string | null;
  branch?: string | null;
  email?: string | null;
  nmls?: string | null;
  licensedStateCount?: number | null;
  joinedDate?: string | null;
  joinedDateTime?: string;
  mode: UserProfileIdentityMode;
  isDirty: boolean;
  isSaving: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
  onPhotoEdit?: () => void;
}

function nameParts(name: string): { first: string; last: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return {
    first: parts[0] ?? '',
    last: parts.length > 1 ? parts[parts.length - 1] : ''
  };
}

function ProfilePhoto({
  photoUrl,
  name,
  onPhotoEdit
}: Pick<UserProfileIdentityHeaderProps, 'photoUrl' | 'name' | 'onPhotoEdit'>) {
  const [imageFailed, setImageFailed] = useState(false);
  const { first, last } = nameParts(name);

  useEffect(() => {
    setImageFailed(false);
  }, [photoUrl]);

  return (
    <div className="lw-user-profile-identity__photo-wrap">
      {photoUrl && !imageFailed ? (
        <img
          className="lw-user-profile-identity__photo"
          src={photoUrl}
          alt={`${name} profile photo`}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span
          className="lw-user-profile-identity__photo lw-user-profile-identity__photo--fallback"
          role="img"
          aria-label={`${name} initials`}
          style={{ background: avatarColor(name) }}
        >
          {initials(first, last)}
        </span>
      )}
      {onPhotoEdit && (
        <button
          className="lw-user-profile-identity__photo-edit"
          type="button"
          aria-label={`Edit profile photo for ${name}`}
          onClick={onPhotoEdit}
        >
          <Camera aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** Presentation-only identity header for the canonical user profile screen. */
export function UserProfileIdentityHeader({
  photoUrl,
  name,
  statusLabel,
  statusTone,
  title,
  branch,
  email,
  nmls,
  licensedStateCount,
  joinedDate,
  joinedDateTime,
  mode,
  isDirty,
  isSaving,
  onSave,
  onDiscard,
  onPhotoEdit
}: UserProfileIdentityHeaderProps) {
  const trimmedBranch = branch?.trim();
  const branchLabel = trimmedBranch
    ? /\bbranch$/i.test(trimmedBranch) ? trimmedBranch : `${trimmedBranch} branch`
    : null;
  const employmentDetails = [title, branchLabel].filter((value): value is string => Boolean(value?.trim()));
  const showEmail = mode !== 'partner_delegated' && Boolean(email?.trim());
  const saveButtonType = onSave ? 'button' : 'submit';

  return (
    <header
      className="lw-user-profile-identity"
      data-mode={mode}
      aria-busy={isSaving || undefined}
    >
      <ProfilePhoto photoUrl={photoUrl} name={name} onPhotoEdit={onPhotoEdit} />

      <div className="lw-user-profile-identity__body">
        <div className="lw-user-profile-identity__heading-row">
          <h1 className="lw-user-profile-identity__name">{name}</h1>
          <LwStatusPill label={statusLabel} tone={statusTone} />
        </div>

        {employmentDetails.length > 0 && (
          <ul className="lw-user-profile-identity__employment" aria-label="Employment details">
            {employmentDetails.map((detail, index) => <li key={`${detail}-${index}`}>{detail}</li>)}
          </ul>
        )}

        <ul className="lw-user-profile-identity__metadata" aria-label="User profile details">
          {showEmail && <li>{email}</li>}
          {nmls?.trim() && <li className="lw-user-profile-identity__nmls">NMLS {nmls}</li>}
          {licensedStateCount !== null && licensedStateCount !== undefined && (
            <li>{licensedStateCount} licensed {licensedStateCount === 1 ? 'state' : 'states'}</li>
          )}
          {joinedDate?.trim() && (
            <li>
              <span>Joined</span>
              {' '}
              <time dateTime={joinedDateTime ?? joinedDate}>{joinedDate}</time>
            </li>
          )}
        </ul>
      </div>

      <div className="lw-user-profile-identity__actions">
        {isDirty && onDiscard && (
          <LwButton
            className="lw-user-profile-identity__discard"
            variant="sm"
            disabled={isSaving}
            onClick={onDiscard}
          >
            Discard
          </LwButton>
        )}
        <LwButton
          variant="primary"
          type={saveButtonType}
          disabled={!isDirty || isSaving}
          onClick={onSave}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </LwButton>
      </div>
    </header>
  );
}
